import { type Connection, PublicKey } from "@solana/web3.js";
import { computed, markRaw, ref } from "vue";
import { fogoSession } from "./useFogoSession";
import { createSolanaConnection } from "@/api/solanaRpc";
import { WSOL_TOKEN } from "@/types/constants";
import type { CommonResponse } from "@/types/response";
import { solanaTokenService } from "@/services/solanaTokenService";
import type { Token } from "@/types/token";
import { useWallet as useSolanaWallet } from "@alphabatem/vue2-wallet-adapter";
import { useWalletOperations } from "./useWalletOperations";

export function useWallet(
  walletStore: ReturnType<typeof useSolanaWallet> = useSolanaWallet(),
) {
  // computed
  // const isConnected = computed<boolean>(() => {
  //   return wallet.connected.value;
  // });
  const getOwner = computed<PublicKey | undefined>(() => {
    const pk = (wallet.publicKey.value ?? wallet.publicKey) as PublicKey;
    return pk ?? PublicKey.default;
  });
  const getTokenBalance = computed<(mint: PublicKey) => number>(() => {
    return (mint: PublicKey) => {
      if (mint.equals(WSOL_TOKEN.mint)) {
        const wSol = balances.value.get(WSOL_TOKEN.mint.toString()) || 0;
        return wSol + solBalance.value;
      }
      return balances.value.get(mint.toString()) || 0;
    };
  });
  const getConnection = computed<Connection>(() => {
    return connection;
  });
  const getFogoWalletPublicKey = computed<PublicKey>(() => {
    if (fogoSession.session.value) {
      return fogoSession.session.value.sessionPublicKey;
    }

    const pk = (wallet.publicKey.value ?? wallet.publicKey) as PublicKey;
    return pk ?? PublicKey.default;
  });
  const getFogoPayerPublicKey = computed<PublicKey>(() => {
    const pk = (wallet.publicKey.value ?? wallet.publicKey) as PublicKey;
    return fogoSession.session.value?.payer || pk || PublicKey.default;
  });

  // data
  const connection = createSolanaConnection();
  const wallet = markRaw(walletStore);
  const { sendTransaction } = useWalletOperations();
  const balances = ref<Map<string, number>>(
    new Map([[WSOL_TOKEN.mint.toString(), 0]]),
  );
  const solBalance = ref<number>(0);

  // methods
  async function getSOLBalance(): Promise<CommonResponse<number>> {
    const res = await solanaTokenService.getSOLBalance({
      connection: getConnection.value,
      owner: getOwner.value,
    });

    if (res.ok) {
      solBalance.value = res.data;
    }

    return res;
  }
  async function getTokensBalance(
    tokens: Token[],
  ): Promise<CommonResponse<Map<string, number>>> {
    const res = await solanaTokenService.getTokensBalance({
      connection: getConnection.value,
      owner: getOwner.value,
      tokens,
    });

    if (res.ok) {
      // update balances
      balances.value = new Map([...balances.value, ...res.data]);
    }

    return res;
  }

  return {
    wallet,
    // isConnected,
    balances,
    solBalance,
    getTokenBalance,
    getFogoWalletPublicKey,
    getFogoPayerPublicKey,
    getConnection,
    getOwner,
    getTokensBalance,
    getSOLBalance,
    sendTransaction,
  };
}
